import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { AppConfig, APP_CONFIG_TOKEN } from "libs/config/app.lib.config";
import { BenefitModel, BenefitName, CustomerBenefitsModel } from "../models/benefit.model";
import { ProductKey } from "libs/product/src/lib/models/product.model";

@Injectable({
	providedIn: "root"
})
export class BenefitService {
	private httpClient = inject(HttpClient);
	private config = inject<AppConfig>(APP_CONFIG_TOKEN);

	applyPromotionToCustomer(promotionCode: string): Observable<CustomerBenefitsModel> {
		return this.httpClient.post<CustomerBenefitsModel>(`${this.config.apiUrl}/customer-benefits`, {
			promotionCode: promotionCode
		});
	}

	applyPromotionToCustomerAsAdmin(customerId: string, promotionCode: string): Observable<CustomerBenefitsModel> {
		return this.httpClient.post<CustomerBenefitsModel>(`${this.config.apiUrl}/customer-benefits/admin/apply-promotion`, {
			customerId: customerId,
			promotionCode: promotionCode
		});
	}

	applyBenefitsToCustomerAsAdmin(customerId: string, benefitNames: string[]): Observable<CustomerBenefitsModel> {
		return this.httpClient.post<CustomerBenefitsModel>(`${this.config.apiUrl}/customer-benefits/admin/apply-benefits`, {
			customerId: customerId,
			benefitNames: benefitNames
		});
	}

	getBenefitsByPromotionCode(promotionCode: string): Observable<Partial<BenefitModel>[]> {
		return this.httpClient.get<Partial<BenefitModel>[]>(
			`${this.config.apiUrl}/benefits/promotion?code=${promotionCode}`
		);
	}

	getBenefitsApplicableByAdmin(): Observable<BenefitModel[]> {
		const queryParams = {
			applicableByAdmin: true
		}
		return this.httpClient.get<BenefitModel[]>(
			`${this.config.apiUrl}/benefits`,
			{ params: queryParams }
		);
	}

	getBenefits(): Observable<BenefitModel[]> {
		return this.httpClient.get<BenefitModel[]>(`${this.config.apiUrl}/benefits`);
	}

	createBenefit(benefit: BenefitModel): Observable<BenefitModel> {
		return this.httpClient.post<BenefitModel>(`${this.config.apiUrl}/benefits`, benefit);
	}

	updateBenefit(id: string, benefit: BenefitModel): Observable<BenefitModel> {
		return this.httpClient.put<BenefitModel>(`${this.config.apiUrl}/benefits/${id}`, benefit);
	}

	deleteBenefit(id: string): Observable<void> {
		return this.httpClient.delete<void>(`${this.config.apiUrl}/benefits/${id}`);
	}

	getCustomerBenefits(): Observable<CustomerBenefitsModel> {
		return this.httpClient.get<CustomerBenefitsModel>(`${this.config.apiUrl}/customer-benefits`);
	}

	getAllCustomerBenefits(): Observable<CustomerBenefitsModel[]> {
		return this.httpClient.get<CustomerBenefitsModel[]>(`${this.config.apiUrl}/customer-benefits/all`);
	}

	getValidBenefitNamesForProductKey(productKey: ProductKey): Observable<BenefitName[]> {
		const queryParams = {
			productKey: productKey
		}
		return this.httpClient.get<BenefitName[]>(
			`${this.config.apiUrl}/customer-benefits/validBenefits`,
			{ params: queryParams }
		);
	}

	isBenefitInactive(benefit: BenefitModel): boolean {
		if(benefit.endDate && new Date(benefit.endDate) < new Date()) {
			return true;
		}
		if(benefit.quantity && benefit.usedQuantity !== undefined && benefit.usedQuantity >= benefit.quantity) {
			return true;
		}
		return false;
	}
}
